import React, { useState } from 'react';
import { useParams, Link } from 'react-router';
import { DGPTAlertRun } from '../../types/dgptAlerts';
import { useAlertConfigById } from './hooks/useAlertConfigs';
import { useAlertRuns } from './hooks/useAlertRuns';
import { useAlertEventInstances } from './hooks/useAlertEventInstances';
import PageTitle from '../headers/PageTitle';
import { Spinner } from 'react-bootstrap';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import ReactPaginate from 'react-paginate';

const AlertRunTable: React.FC = () => {
  const { alert_config_id: alertConfigId } = useParams<{
    alert_config_id: string;
  }>();
  const { data: config, isLoading: isLoadingConfig } = useAlertConfigById(
    alertConfigId || ''
  );
  const { data: runs, isLoading: isLoadingRuns } = useAlertRuns({
    configId: alertConfigId || '',
  });
  const { data: instances } = useAlertEventInstances(alertConfigId || '');
  const [_selectedRun, _setSelectedRun] = useState<DGPTAlertRun | null>(null);
  const [_showDetailsModal, _setShowDetailsModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const PER_PAGE = 10;

  const columnHelper = createColumnHelper<DGPTAlertRun>();

  const columns = [
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      enableSorting: true,
      cell: (info) => {
        const status = info.getValue();
        const cls =
          status === 'Completed'
            ? 'success'
            : status === 'Failed'
              ? 'danger'
              : 'warning';
        return <span className={`badge bg-${cls}`}>{status}</span>;
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Created At',
      enableSorting: true,
      cell: (info) => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor('systemTriggered', {
      id: 'systemTriggered',
      header: 'Trigger Type',
      cell: (info) => (info.getValue() ? 'System' : 'Manual'),
    }),
    columnHelper.accessor('dgptAlertEventInstanceId', {
      id: 'dgptAlertEventInstanceId',
      header: 'SITREP',
      cell: (info) => {
        const val = info.getValue();
        if (!val) return 'No SITREP';
        const instance = instances?.find((i) => i.id === val);
        if (!instance?.dgptSitrepId) return 'No SITREP';
        return (
          <Link to={`/sitreps/${instance.dgptSitrepId}`}>View SITREP</Link>
        );
      },
    }),
  ] as ColumnDef<DGPTAlertRun>[];

  if (isLoadingConfig || isLoadingRuns) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!config) {
    return <div>Alert configuration not found</div>;
  }

  const filteredRuns: DGPTAlertRun[] = runs || [];
  const hasRuns = filteredRuns.length > 0;

  return (
    <div className="p-4 pt-0">
      <PageTitle
        title={`Alert Logs - ${config.name}`}
        parents={['Alert Configurations', config?.name || 'Loading...']}
      />
      <p className="text-muted mb-4">
        Each time DisasterGPT checks for alert conditions, a log entry is
        created. When conditions match a trigger, a{' '}
        <Link to={`/aialerts/${alertConfigId}/reports`}>Report</Link> is
        generated and a <Link to="/sitreps">SITREP</Link> is created or updated.
        The logs show the history of all checks for this Configuration, whether
        they resulted in an alert or not.
      </p>
      <div className="bg-neutral-800 rounded-1 p-3">
        {!hasRuns && (
          <div className="alert alert-info mb-3">
            <strong>No Alert Logs Found</strong>
            <p className="mb-0">There are no alert logs to display.</p>
          </div>
        )}

        {hasRuns && (
          <>
            <PratusTable
              className={'tbody-p-0 layout-auto'}
              columns={columns}
              data={filteredRuns.slice((page - 1) * PER_PAGE, page * PER_PAGE)}
              getRowId={(row) => String(row.id)}
              noDataIndication="No alert logs found"
            />
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={'\u00ab'}
                nextLabel={'\u00bb'}
                breakLabel={'...'}
                pageCount={Math.max(
                  1,
                  Math.ceil(filteredRuns.length / PER_PAGE)
                )}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={(sel) => setPage(sel.selected + 1)}
                forcePage={Math.max(
                  0,
                  Math.min(
                    page - 1,
                    Math.ceil(filteredRuns.length / PER_PAGE) - 1
                  )
                )}
                containerClassName={'pagination'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AlertRunTable;
